import { useCallback, useEffect, useState } from 'react';
import { Image as ImageIcon, Loader2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useBusiness } from '../contexts/BusinessContext';
import {
  deleteMedia,
  listMediaWithPlatformUsage,
  type MediaItem,
  type MediaItemWithUsage,
  type MediaPlatform,
} from '../lib/mediaLibrary';
import AddMediaOptions from './AddMediaOptions';
import MediaTileMedia from './MediaTileMedia';
import { useImageViewer } from './ImageViewerProvider';
import { isVideoMedia } from '../lib/mediaLibrary';

const PLATFORM_BADGE: Record<MediaPlatform, string> = {
  google: 'bg-blue-600 text-white',
  facebook: 'bg-[#1877F2] text-white',
  instagram: 'bg-gradient-to-r from-purple-600 to-pink-500 text-white',
  linkedin: 'bg-[#0A66C2] text-white',
};

function MediaGrid({
  items,
  deletingId,
  onDelete,
}: {
  items: MediaItemWithUsage[];
  deletingId: string | null;
  onDelete: (item: MediaItemWithUsage) => void;
}) {
  const { open: openImageViewer } = useImageViewer();

  if (items.length === 0) {
    return (
      <div className="bg-white border border-dashed border-gray-200 rounded-2xl px-4 py-10 text-center">
        <ImageIcon className="h-7 w-7 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">No images match</p>
      </div>
    );
  }

  // Videos don't go through the image viewer — open the file directly.
  const openAt = (index: number) => {
    const item = items[index];
    if (item && isVideoMedia(item)) {
      window.open(item.url, '_blank', 'noopener');
      return;
    }
    const images = items.filter((it) => !isVideoMedia(it));
    const imageIndex = images.findIndex((it) => it.id === item?.id);
    openImageViewer({
      images: images.map((it) => ({
        src: it.url,
        caption: it.file_name || it.alt || 'Media',
        download: it.file_name || undefined,
      })),
      index: Math.max(0, imageIndex),
    });
  };

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2 sm:gap-2.5">
      {items.map((item, index) => (
        <div
          key={item.id}
          className="group bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
        >
          <div className="relative aspect-square bg-gray-50">
            <button
              type="button"
              onClick={() => openAt(index)}
              className="absolute inset-0 z-0 cursor-pointer"
              aria-label={`View ${item.file_name || 'image'}`}
            >
              <MediaTileMedia
                url={item.url}
                mimeType={item.mime_type}
                durationSeconds={item.duration_seconds}
                alt={item.alt || item.file_name || 'Media'}
              />
            </button>
            {item.platformUsage.length > 0 ? (
              <div className="absolute top-2 left-2 z-[1] right-10 flex flex-wrap gap-1 pointer-events-none">
                {item.platformUsage.map((tag) => (
                  <span
                    key={`${tag.platform}-${tag.connectionId}`}
                    className={`max-w-full px-1.5 py-0.5 rounded-md text-[10px] font-semibold shadow-sm leading-tight truncate ${PLATFORM_BADGE[tag.platform]}`}
                    title={tag.usedAt ? `Used ${new Date(tag.usedAt).toLocaleString()}` : tag.label}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            ) : null}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item);
              }}
              disabled={deletingId === item.id}
              className="absolute top-2 right-2 z-[1] h-8 w-8 rounded-lg bg-white/95 text-gray-600 hover:text-red-600 flex items-center justify-center shadow-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity disabled:opacity-50"
              aria-label="Delete image"
            >
              {deletingId === item.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
          <div className="px-2.5 py-2">
            <p className="text-[11px] text-gray-700 truncate font-medium">
              {item.file_name || 'Untitled'}
            </p>
            {(item.width || item.height) && (
              <p className="text-[10px] text-gray-400 mt-0.5">
                {item.width || '?'}×{item.height || '?'}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MediaManagerLibrary() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [media, setMedia] = useState<MediaItemWithUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!businessId) {
        setMedia([]);
        setLoading(false);
        return;
      }
      if (!opts?.silent) setLoading(true);
      try {
        const items = await listMediaWithPlatformUsage(businessId);
        setMedia(items);
      } catch (err) {
        console.error(err);
        if (!opts?.silent) toast.error('Failed to load media');
      } finally {
        setLoading(false);
      }
    },
    [businessId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const mergeAdded = (created: MediaItem[]) => {
    // Show new items instantly from the rows the upload already returned —
    // don't gate the UI on a refetch, which can stall or fail right as the
    // iOS WebView resumes from the native photo picker.
    setMedia((prev) => {
      const existing = new Set(prev.map((m) => m.id));
      const fresh = created
        .filter((c) => !existing.has(c.id))
        .map((c) => ({ ...c, platformUsage: [] }) as MediaItemWithUsage);
      return [...fresh, ...prev];
    });
    // Background sync to pick up usage tags without blanking the grid.
    void load({ silent: true });
  };

  const handleDelete = async (item: MediaItemWithUsage) => {
    if (!confirm(`Remove “${item.file_name || 'this image'}” from your media library?`)) return;
    setDeletingId(item.id);
    try {
      await deleteMedia(item.id);
      setMedia((prev) => prev.filter((m) => m.id !== item.id));
      toast.success('Image removed');
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete image');
    } finally {
      setDeletingId(null);
    }
  };

  if (!businessId) {
    return (
      <p className="text-sm text-gray-500 py-10 text-center">Select a business to manage media.</p>
    );
  }

  return (
    <div className="space-y-3">
      {loading ? (
        <div className="flex justify-center py-16 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : media.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl px-6 py-14 text-center">
          <ImageIcon className="h-8 w-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900">No media yet</p>
          <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
            Upload photos or choose from images already in bookings, quotes, and jobs.
          </p>
          <div className="mt-5 mx-auto max-w-sm text-left rounded-xl border border-gray-200 overflow-hidden bg-white">
            <AddMediaOptions businessId={businessId} variant="dropdown" allowVideo onAdded={mergeAdded} />
          </div>
        </div>
      ) : (
        <section className="space-y-2.5">
          <p className="text-xs text-gray-500 text-right">
            {media.length} photo{media.length === 1 ? '' : 's'}
          </p>
          <MediaGrid
            items={media}
            deletingId={deletingId}
            onDelete={(item) => void handleDelete(item)}
          />
        </section>
      )}
    </div>
  );
}
